import React, { useEffect } from 'react'
import { iEmailCampaignOverview } from '../../types/internal/email/campaign';
import { showToast } from '../Toast';
import i18n from '../../i18n';

export function validateWords(value: string, max_length: number) {
    if (value.split(' ').length <= max_length) {
        return null;
    }
    return "You have reached the maximum word limit"
}

export const getFlatKeys = (o: any, prefix = ''): string[] => {
    return Object.keys(o).flatMap(k => {
        const dot = prefix ? '.' : '';
        const new_k = isNaN(+k) ? `${dot}${k}` : `[${k}]`;
        const new_prefix = `${prefix}${new_k}`;
        return Object(o[k]) === o[k] ? getFlatKeys(o[k], new_prefix) : [new_prefix]
    });
}

export function getNestedValue(original_data: any, original_key: string, default_value: any = "-") {
    const keys = original_key.split('.');
    let data = { ...original_data };
    keys.forEach(key => {
        data = data ? data[key] : default_value;
    });
    return data;
}

function getStatus(status: string) {
    const normalized = (status || "").toLowerCase();
    const known: Record<string, string> = {
        new: "common:statusCapsule.needsAttention",
        active: "common:statusCapsule.active",
        subscribed: "common:statusCapsule.active",
        "session closed": "common:statusCapsule.sessionClosed",
        ready: "common:statusCapsule.ready",
        done: "common:statusCapsule.done",
        "on going": "common:statusCapsule.onGoing",
        error: "common:statusCapsule.error",
        gray: "common:statusCapsule.gray",
        web: "common:statusCapsule.web",
        mobile: "common:statusCapsule.mobile",
        embed: "common:statusCapsule.embed",
        paid: "common:statusCapsule.paid",
        approved: "common:statusCapsule.approved",
        pending: "common:statusCapsule.pending",
        inactive: "common:statusCapsule.inactive",
    };
    const key = known[normalized];
    if (key) {
        return i18n.t(key);
    }
    const rawKey = `common:statusCapsule.raw.${normalized.replace(/\s+/g, "_")}`;
    return i18n.t(rawKey, { defaultValue: capitalizeFirstLetter(status.toString()) });
}
function capitalizeFirstLetter(str: string) {
    if (str.length === 0) {
        return ""; // Handle empty strings
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
}
function getStatusColor(status: string) {
    // add match for below code 
    switch (status) {
        case 'new': return "#fadede";
        case 'Session closed': return "#eaf7c9";
        case "active": return "#eaf7c9";
        case "subscribed": return "#eaf7c9";
        case "ready": return "#f7eec9";
        case 'done': return "#eaf7c9";
        case 'on going': return "#eaf7c9";
        case "error": return "#fadede";
        case "gray": return "#f1f1f1";
        case "web": return "#eaf7c9";
        case "mobile": return "#eaf7c9";
        case "embed": return "#eaf7c9";
        case "paid": return "#eaf7c9";
        case "approved": return "#eaf7c9";
        case "unsubscribed": return "#fadede";
        default: return "#f1f1f1";
    }

    // if (status === "new") {
    //     return "#dc3545"
    // }
    // if (status === "done") {
    //     return "#eaf7c9"
    // } else if (status === "on going") {
    //     return "#eaf7c9"
    // } else {
    //     return "fadede"
    // }
}


function getStatusTextColor(status: string) {
    // add match for below code 
    switch (status) {
        case 'new': return "#840606";
        case 'Session closed': return "#618406";
        case "active": return "#618406";
        case "subscribed": return "#618406";
        case "ready": return "#846e06";
        case 'done': return "#618406";
        case 'on going': return "#618406";
        case "error": return "#840606";
        case "gray": return "#121212";
        case "web": return "#618406";
        case "mobile": return "#618406";
        case "embed": return "#618406";
        case "paid": return "#618406";
        case "approved": return "#618406";
        case "unsubscribed": return "#840606";
        default: return "#121212";
    }

    // if (status === "new") {
    //     return "#dc3545"
    // }
    // if (status === "done") {
    //     return "#eaf7c9"
    // } else if (status === "on going") {
    //     return "#eaf7c9"
    // } else {
    //     return "fadede"
    // }
}

export function statusColor(color: string) {
    switch (color) {
        case "red": return { color: "#840606", background: "#fadede" };
        case "green": return { color: "#618406", background: "#eaf7c9" };
        case "yellow": return { color: "#846e06", background: "#f7eec9" };
        default: return { color: "#121212", background: "#f1f1f1" };
    }
}

export function statusCapsuleWithColor(status: string, color: string = "") {
    return <div style={{
        textAlign: 'right',
        display: 'flex',
        flexFlow: 'column',
        width: 'fit-content',
        alignItems: 'start',
        justifyContent: 'center',
    }}>
        <div
            style={statusColor(color)}
            className="campaign-status-badge"
        >
            {getStatus(status + "")}
        </div>
    </div>
}

export function statusCapsule(status: string) {
    return <div style={{
        textAlign: 'right',
        display: 'flex',
        flexFlow: 'column',
        width: 'fit-content',
        alignItems: 'start',
        justifyContent: 'center',
    }}>
        <div
            style={{
                background: getStatusColor(status + ""),
                color: getStatusTextColor(status + ""),
            }}
            className="campaign-status-badge"
        >
            {getStatus(status + "")}
        </div>
    </div>
}


export const useDebouncedEffect = (effect : Function, deps :any, delay : any) => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
}

/**
 * Converts an image URL to a Base64 data URL
 * @param url - The image URL
 * @returns Promise<string> - Base64-encoded data URL
 */
export const imageUrlToBase64 = async (url: string): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Allow cross-domain access if CORS headers exist
        img.src = url;

        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('Canvas context not available');
                ctx.drawImage(img, 0, 0);
                const dataURL = canvas.toDataURL('image/png');
                resolve(dataURL);
            } catch (err) {
                reject(err);
            }
        };

        img.onerror = (error) => {
            reject(new Error(`Failed to load image from URL: ${url}`));
        };
    });
};

export const domainToast =(resp:any) => {
    console.log(resp);
const res = resp.server;
console.log("data", res);

// Use a consistent value check (e.g., res.status === 0 for 'not verified')
// Assuming 'verified' means a truthy value or a specific number (e.g., 1)
// and 'not verified' means a falsy value or a specific number (e.g., 0).

// Let's assume the value should be truthy for 'verified'.
// If 0 means 'not verified' and 1 means 'verified', your original check was complex.
// Based on your original code's structure, it seems you are trying to check if ANY of them are *not* verified.

const spfVerified = res.spf_verified; // Corrected typo from original: dkim_verified != 0
const domainVerified = res.domain_verified;
const dkimVerified = res.dkim_verified;

// Check if ANY of the three required fields are NOT verified (i.e., are falsy or 0)
if (!spfVerified || !domainVerified || !dkimVerified) {
    const unverifiedList = [];

    if (!spfVerified) {
        unverifiedList.push('SPF');
    }
    if (!domainVerified) {
        unverifiedList.push('Domain');
    }
    if (!dkimVerified) {
        unverifiedList.push('DKIM');
    }

    // Join the list into a comma-separated string for the toast message
    const unverifiedString = unverifiedList.join(', ');
    showToast(
        'Error',
        i18n.t('notifications.dnsNotVerified', { ns: 'common', list: unverifiedString }),
        i18n.t('notifications.errorTitle', { ns: 'common' })
    );
} else {
    // All three are verified (truthy/non-zero)
    showToast(
        'Success',
        i18n.t('notifications.dnsVerified', { ns: 'common' }),
        i18n.t('notifications.successTitle', { ns: 'common' })
    );
}
}

export const popupWindow = (url:string, title:string, w:number, h:number) => {
  var left = (window.screen.width/2)-(w/2);
  var top = 0;
  return window.open(url, title, 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width='+w+', height='+window.screen.height+', top='+top+', left='+left);
}

export const LogoTint =  ({
  src,
  accent,
  style = '',
}:  {
  src: string;
  accent: string;
  style?: string;
}) : string => {
    let url = `url(${src})`;

  return (
    `<div
      style="
        ${style}
        background-color: ${accent};
        -webkit-mask-image: ${url};
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-size: contain;
        -webkit-mask-position: center;
        mask-image: ${url};
        mask-repeat: no-repeat;
        mask-size: contain;
        mask-position: center;
      "
    />`
  );
};